// frontend/src/hooks/useForecastData.js
// Hooks for forecast/demand time-series data.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from '../contexts/AuthContext';
import { kpi as kpiKey, timelines as timelinesKey, forecastStatistics as forecastStatsKey } from '../queries/keys';
import { useTimeSeriesFetcher } from './useTimeSeriesFetcher';
import { scopeParams } from '../features/item-filter/scopeParams';
import { readResult } from '../queries/api';
import { useTabPollInterval } from '../tabs/tabsContexts';

const THIRTY_SEC = 30 * 1000;
const SIXTY_SEC = 60 * 1000;

/**
 * Fetch forecast KPI: current-month total demand (qty + value).
 * Used by collapsed tile summary on the Forecast page.
 *
 * Caching matches the locked-in defaults: 30s staleTime + 60s polling
 * while the tab is on screen. See useInventoryKpi for the rationale.
 */
export function useForecastKpi() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: kpiKey.forecast(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/forecasts/kpi/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Demand timeline: sales history (solid) → forecast demand (dashed).
 */
export function useDemandTimeline({ metric, historyMonths = 12, projectionMonths = 11, scope }) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const params = {
    metric,
    history_months: String(historyMonths),
    projection_months: String(projectionMonths),
    ...scopeParams(scope),
  };

  return useTimeSeriesFetcher({
    queryKey: timelinesKey.demand(storeId, params),
    endpoint: '/api/forecasts/demand-timeline/',
    params,
    enabled: !!storeId,
  });
}

/**
 * Forecast generator statistics: forecast count, last run timestamp,
 * eligible-item counts, etc. Mount-on-page-render data on the
 * Forecast Generator page.
 *
 * Cached so navigating away and back within 30s reuses the existing
 * data; after a forecast run the page calls refetch().
 */
export function useForecastStatistics() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: forecastStatsKey.all(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/forecasts/statistics/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    enabled: !!storeId,
  });
  return readResult(query, t);
}
